/**
 * PURPOSE: CRUD de oficiales de partido (árbitros y demás roles) para UNA compañía, solo rol 1.
 * RESPONSIBILITIES: Crear/editar/activar/desactivar oficiales de la compañía recibida por prop y
 *   asignar su elegibilidad por familia deportiva (FTR-0004), sin exponer la tabla de elegibilidad
 *   al usuario. Un oficial habilitado para una familia queda seleccionable para todos los roles
 *   activos de esa familia — ya no existe una elegibilidad por rol individual.
 * COLLABORATORS: matchOfficialsService.js, AdminOfficialsTab.jsx (Master global, rol 1, con
 *   selector de compañía — único punto de entrada en V1).
 * ROUTE: winscore/src/admin/AdminCompany/CompanyOfficialsTab.jsx
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import {
  createOfficial,
  getApiErrorMessage,
  listCompanyOfficials,
  listRoleCatalog,
  replaceOfficialSportEligibility,
  setOfficialState,
  updateOfficial,
} from "../../services/matchOfficialsService.js";

const EMPTY_FORM = { firstname: "", lastname: "", documentNumber: "" };

const CompanyOfficialsTab = ({ idcompany }) => {
  const [officials, setOfficials] = useState([]);
  const [sportFamilies, setSportFamilies] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sportsOpenFor, setSportsOpenFor] = useState(null);
  const [sportsDraft, setSportsDraft] = useState([]);
  const [savingSports, setSavingSports] = useState(false);

  const loadOfficials = useCallback(async () => {
    if (!idcompany) return;
    setLoading(true);
    try {
      setOfficials(await listCompanyOfficials(idcompany, true));
    } catch (error) {
      console.error("Error al cargar oficiales", error);
      toast.error(getApiErrorMessage(error, "No se pudieron cargar los oficiales."));
    } finally {
      setLoading(false);
    }
  }, [idcompany]);

  useEffect(() => {
    loadOfficials();
  }, [loadOfficials]);

  useEffect(() => {
    listRoleCatalog()
      .then((catalog) => setSportFamilies(catalog?.sportFamilies || []))
      .catch((error) => console.error("Error al cargar catálogo de deportes", error));
  }, []);

  const filteredOfficials = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return officials;
    return officials.filter((official) =>
      [official.firstname, official.lastname, official.documentNumber].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [search, officials]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
  };

  const beginEdit = (official) => {
    setEditing(official);
    setForm({
      firstname: official.firstname || "",
      lastname: official.lastname || "",
      documentNumber: official.documentNumber || "",
    });
    setSportsOpenFor(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.firstname.trim() || !form.lastname.trim()) {
      toast.error("Nombres y apellidos son obligatorios.");
      return;
    }
    if (!form.documentNumber.trim()) {
      toast.error("El número de documento es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await updateOfficial(editing.idofficial, {
          firstname: form.firstname.trim(),
          lastname: form.lastname.trim(),
          documentNumber: form.documentNumber.trim(),
        });
        toast.success("Oficial actualizado correctamente.");
      } else {
        await createOfficial({
          idcompany,
          firstname: form.firstname.trim(),
          lastname: form.lastname.trim(),
          documentNumber: form.documentNumber.trim(),
        });
        toast.success("Oficial creado correctamente.");
      }
      resetForm();
      await loadOfficials();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo guardar el oficial."));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (official) => {
    const isActive = official.active;
    const action = isActive ? "desactivar" : "reactivar";
    if (!window.confirm(`¿Desea ${action} a ${official.firstname} ${official.lastname}?`)) return;

    setSaving(true);
    try {
      await setOfficialState(official.idofficial, !isActive);
      toast.success(`Oficial ${isActive ? "desactivado" : "reactivado"}.`);
      if (editing?.idofficial === official.idofficial) resetForm();
      await loadOfficials();
    } catch (error) {
      toast.error(getApiErrorMessage(error, `No se pudo ${action} al oficial.`));
    } finally {
      setSaving(false);
    }
  };

  const openSports = (official) => {
    setSportsOpenFor(official.idofficial);
    setSportsDraft(official.eligibleSportFamilyKeys || []);
  };

  const toggleSportDraft = (key) => {
    setSportsDraft((current) =>
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    );
  };

  const saveSports = async (official) => {
    setSavingSports(true);
    try {
      await replaceOfficialSportEligibility(official.idofficial, sportsDraft);
      toast.success("Deportes actualizados correctamente.");
      setSportsOpenFor(null);
      await loadOfficials();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudieron actualizar los deportes."));
    } finally {
      setSavingSports(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {editing ? "Editar oficial" : "Nuevo oficial"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              Árbitros y demás oficiales de partido de la organización.
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800"
            >
              Nuevo
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Nombres *
            <input
              name="firstname"
              value={form.firstname}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Apellidos *
            <input
              name="lastname"
              value={form.lastname}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            N.º de documento *
            <input
              name="documentNumber"
              value={form.documentNumber}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear oficial"}
            </button>
          </div>
        </form>
      </section>

      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            Oficiales de la Organización
          </h2>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar oficial..."
            className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
        ) : filteredOfficials.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            No hay oficiales registrados.
          </div>
        ) : (
          <div className="space-y-2">
            {filteredOfficials.map((official) => {
              const active = official.active;
              const sportsOpen = sportsOpenFor === official.idofficial;
              return (
                <article
                  key={official.idofficial}
                  className={`rounded-lg border p-3 ${
                    active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"
                  }`}
                >
                  <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate font-semibold text-slate-800">
                          {official.lastname}, {official.firstname}
                        </h3>
                        {!active && (
                          <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <p className="truncate text-sm text-slate-500">
                        Doc.: {official.documentNumber || "—"} ·{" "}
                        {official.eligibleSportFamilyKeys.length} deporte(s) habilitado(s)
                      </p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => (sportsOpen ? setSportsOpenFor(null) : openSports(official))}
                        className="rounded-lg border border-indigo-200 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        {sportsOpen ? "Cerrar deportes" : "Deportes"}
                      </button>
                      <button
                        type="button"
                        onClick={() => beginEdit(official)}
                        className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(official)}
                        disabled={saving}
                        className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                          active
                            ? "border-red-200 text-red-600 hover:bg-red-50"
                            : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        }`}
                      >
                        {active ? "Desactivar" : "Reactivar"}
                      </button>
                    </div>
                  </div>

                  {sportsOpen && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                        Deportes habilitados
                      </h4>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {sportFamilies.map((family) => (
                          <label key={family.key} className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={sportsDraft.includes(family.key)}
                              onChange={() => toggleSportDraft(family.key)}
                            />
                            {family.name}
                          </label>
                        ))}
                      </div>
                      <div className="mt-3 flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setSportsOpenFor(null)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => saveSports(official)}
                          disabled={savingSports}
                          className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
                        >
                          {savingSports ? "Guardando..." : "Guardar deportes"}
                        </button>
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default CompanyOfficialsTab;
